/**
 * Admin page: Manufacturer Warranty Defaults
 * Set the default warranty (years) for each manufacturer + category.
 * The Contract Pack wizard auto-fills warranty fields whenever the customer's
 * panel/inverter/battery manufacturer matches a row here.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Plus, Trash2, Pencil, Loader2, ShieldCheck, Upload } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { CsvImportDialog } from '../components/CsvImportDialog';

const CATEGORIES = [
  { value: 'panel', label: 'Solar Panel' },
  { value: 'inverter', label: 'Inverter' },
  { value: 'battery', label: 'Battery' },
  { value: 'mounting', label: 'Mounting System' },
];

const empty = { manufacturer: '', category: 'panel', warranty_years: '' };

export const ManufacturerWarrantiesContent = ({ embedded = false }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/manufacturer-warranty-defaults');
      setRows(data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const save = async () => {
    if (!editing.manufacturer?.trim() || !editing.warranty_years) {
      toast.error('Manufacturer and warranty years are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        manufacturer: editing.manufacturer.trim(),
        category: editing.category,
        warranty_years: parseInt(editing.warranty_years, 10),
      };
      if (editing.id) await api.put(`/manufacturer-warranty-defaults/${editing.id}`, payload);
      else await api.post('/manufacturer-warranty-defaults', payload);
      toast.success(editing.id ? 'Updated' : 'Added');
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async (id, mfr) => {
    if (!window.confirm(`Delete warranty default for ${mfr}?`)) return;
    try {
      await api.delete(`/manufacturer-warranty-defaults/${id}`);
      toast.success('Deleted');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const grouped = rows.reduce((acc, r) => {
    (acc[r.category] = acc[r.category] || []).push(r);
    return acc;
  }, {});

  return (
    <div data-testid="warranty-defaults-page">
      {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-emerald-600" />
              Manufacturer Warranty Defaults
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              When you select a manufacturer in the Contract Pack wizard, the warranty field auto-fills from this table.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="open-import-csv-btn">
              <Upload className="w-4 h-4 mr-1" /> Import CSV
            </Button>
            <Button
              onClick={() => setEditing({ ...empty })}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="add-warranty-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add Default
            </Button>
          </div>
        </div>
      )}
      {embedded && (
        <div className="flex justify-end gap-2 mb-3">
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="open-import-csv-btn" size="sm">
            <Upload className="w-4 h-4 mr-1" /> Import CSV
          </Button>
          <Button onClick={() => setEditing({ ...empty })} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-warranty-btn" size="sm">
            <Plus className="w-4 h-4 mr-1" /> Add Default
          </Button>
        </div>
      )}

      <div className="space-y-4">
        {loading ? (
          <Card><CardContent className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></CardContent></Card>
        ) : rows.length === 0 ? (
          <Card><CardContent className="py-8 text-center text-sm text-slate-400 italic">No defaults set yet. Click "Add Default".</CardContent></Card>
        ) : (
          CATEGORIES.map((c) => grouped[c.value]?.length ? (
            <Card key={c.value}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px] border-emerald-200 text-emerald-700">{c.label}</Badge>
                  <span className="text-slate-500 font-normal">({grouped[c.value].length})</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5">
                {grouped[c.value].map((r) => (
                  <div key={r.id} className="flex items-center justify-between gap-2 p-2 rounded border border-slate-100 hover:bg-slate-50" data-testid={`warranty-row-${r.id}`}>
                    <div>
                      <span className="font-medium text-sm text-slate-800">{r.manufacturer}</span>
                      <span className="ml-3 text-xs text-slate-500">{r.warranty_years} year warranty</span>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setEditing({ ...r })} className="h-7 text-xs" data-testid={`edit-warranty-${r.id}`}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(r.id, r.manufacturer)} className="h-7 text-xs text-rose-600 hover:bg-rose-50" data-testid={`delete-warranty-${r.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          ) : null)
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="warranty-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit Warranty Default' : 'Add Warranty Default'}</DialogTitle>
            <DialogDescription>
              Wizard auto-fills warranty fields when a customer's manufacturer matches an entry here.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Manufacturer</Label>
                <Input
                  value={editing.manufacturer}
                  onChange={(e) => setEditing({ ...editing, manufacturer: e.target.value })}
                  placeholder="JA Solar / Solis / Pylontech / K2 Systems"
                  data-testid="warranty-mfr-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Category</Label>
                  <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                    <SelectTrigger data-testid="warranty-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Warranty (years)</Label>
                  <Input
                    type="number" min="1"
                    value={editing.warranty_years}
                    onChange={(e) => setEditing({ ...editing, warranty_years: e.target.value })}
                    placeholder="25"
                    data-testid="warranty-years-input"
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="warranty-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editing?.id ? 'Update' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CsvImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        title="Import Manufacturer Warranties from CSV"
        description="Bulk load warranty defaults. Rows are upserted by manufacturer + category (panel / inverter / battery / mounting)."
        templateUrl="/manufacturer-warranty-defaults/template.csv"
        importUrl="/manufacturer-warranty-defaults/import-csv"
        templateFilename="manufacturer-warranties-template.csv"
        onSuccess={reload}
        colorClass="bg-emerald-600 hover:bg-emerald-700"
        iconColor="text-emerald-600"
      />
    </div>
  );
};

export default function ManufacturerWarranties() {
  return (
    <div className="p-6 max-w-5xl mx-auto">
      <ManufacturerWarrantiesContent />
    </div>
  );
}
